import { RoundedButton } from '@components/Buttons';
import { Block, BlockProps, Flex, InlineBlock, InlineFlex } from '@components/Layout/Blocks';
import { Row } from '@components/Layout/Row';
import { ItemType } from '@components/SummarySelection/data/selectors';
import { SummaryEditPackageDialog } from '@components/SummarySelection/SummaryEditPackageDialog';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { FlexProps } from '@lib/style/mixins/flex';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Paper, { PaperProps } from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography/Typography';
import { Package } from '@model/Package';
import { call } from '@packages/functions';
import { Image } from '@packages/image';
import React from 'react';
import styled from 'styled-components';

interface SummaryCardProps extends Partial<BlockProps & FlexProps & PaperProps> {
    item: ItemType;
    onRemove: (item: ItemType) => void;
    onItemChange: (item: ItemType, skus: any[]) => void;
}

/**
 *
 * @param item
 * @param uniqueKey
 * @returns {Array|{image: *, name: *, id: *}}
 */
const mapSku = (item: any | ItemType, uniqueKey = '') => {
    if (!item) {
        return [];
    }

    if (item.type === Package) {
        const subCategories = item.value && item.value.subcategories ? item.value.subcategories : [];
        let k = 0;
        const counts = {};
        const defaultSkusCount = subCategories.reduce(
            (acc, { default_sku }) => ({
                ...acc,
                [default_sku.id]: (acc[default_sku.id] || 0) + 1,
            }),
            {},
        );

        return subCategories.reduce((acc, { default_sku }) => {
            if (!counts[default_sku.id]) {
                counts[default_sku.id] = 0;
            }

            let newName = `${default_sku.name}`;

            if (defaultSkusCount[default_sku.id] > 1) {
                newName += ` #${++counts[default_sku.id]}`;
            }

            return [
                ...acc,
                ...mapSku(
                    {
                        ...(default_sku || {}),
                        name: newName,
                    },
                    `${k++}`,
                ),
            ];
        }, []);
    }

    const { id, name, preview_photo, image } = item.value || item;

    return [{ key: `sku_${uniqueKey}_${id}`, id, name, image: image || preview_photo }];
};

export const SummaryPreviewCard = (props: SummaryCardProps) => {
    const { item, onRemove, onItemChange, ...rest } = props;

    const isMobile = useMediaQuery('mobile');

    if (!item) {
        return null;
    }

    const { name } = item.value || {};
    const handleRemove = () => call(onRemove, item);
    const items = mapSku(item);

    const size = isMobile ? 80 : 140;

    rest.elevation = rest.elevation ?? 1;

    return (
        <PackageCard component={Paper} {...rest}>
            <InlineFlex component={Row} align={'center'} className="package__header" w={'100%'} minHeight={48}>
                <InlineBlock w={'100%'} maxWidth={'calc(100% - 78px)'} p={'0 0 0 14px'}>
                    {item.type === Package ? (
                        <Row align={'center'} justify={'start'} style={{ flexWrap: 'nowrap' }}>
                            <Typography variant={'subtitle2'} style={{ marginRight: 14 }}>
                                {name}
                            </Typography>
                            <SummaryEditPackageDialog item={item} onItemChange={onItemChange}>
                                <RoundedButton color={'primary'}>Edit Items</RoundedButton>
                            </SummaryEditPackageDialog>
                        </Row>
                    ) : (
                        <span>&nbsp;</span>
                    )}
                </InlineBlock>
                <InlineBlock w={78} p={'0 14px 0 30px'}>
                    <IconButton className={'package__header__button'} onClick={handleRemove}>
                        <Icon>close</Icon>
                    </IconButton>
                </InlineBlock>
            </InlineFlex>

            <InlineFlex minHeight={'calc(100% - 48px)'} style={{ whiteSpace: 'nowrap' }} padding={'0 0 0 14px'}>
                {items.map(({ key, name, image }) => (
                    <InlineBlock className={'package__card'} key={key}>
                        {image ? (
                            <Image source={image} fit={'contain'} variant={'thumbnail'} width={size} height={size} />
                        ) : (
                            <Block style={{ width: size, height: size }} />
                        )}
                        <Typography className={'package__card__name'}>{name}</Typography>
                    </InlineBlock>
                ))}
            </InlineFlex>
        </PackageCard>
    );
};

const PackageCard = styled(Flex)`
    && {
        flex-direction: column;
        background-color: #fff;
        padding: 0;
        margin-right: 14px;
        border-radius: 2px;
        min-width: 240px;
        @media screen and (max-width: 599px) {
            min-width: 140px;
        }
        &:last-child {
            margin-right: 0;
        }

        .package {
            &__card {
                border: 1px solid #dedede;
                border-radius: 3px;
                padding: 14px;
                margin: 0 14px 14px 0;
                width: 212px;
                text-align: center;
                @media screen and (max-width: 599px) {
                    width: 140px;
                }
                &__name {
                    white-space: normal !important;
                }
            }
            &__header {
                &__button {
                    width: 34px;
                    height: 34px;
                    line-height: 34px;
                    padding: 5px;
                    margin: 0;
                }
            }
        }
    }
`;
